import { supabase } from './supabase';
import type { Webpage } from '@/types';

export const WEBPAGE_READ_COLUMNS = `
  id,
  url,
  canonical_url,
  title,
  description,
  image,
  image_storage_path,
  image_source,
  site_name,
  type,
  author,
  content,
  created_at,
  user_id,
  favicon,
  favicon_storage_path,
  notes,
  check_later,
  published_at
`;

export async function addPlaceholderWebpage(userId: string): Promise<Webpage | null> {
  const placeholderData = {
    url: 'https://example.com',
    title: 'Example Webpage',
    description: 'This is a placeholder webpage for testing purposes',
    image: 'https://via.placeholder.com/300',
    site_name: 'Example Site',
    type: 'article',
    author: 'Test Author',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
    user_id: userId,
    favicon: 'https://example.com/favicon.ico'
  };

  const { data, error } = await supabase
    .from('webpages')
    .insert([placeholderData])
    .select()
    .single();

  if (error) {
    console.error('Error adding placeholder webpage:', error);
    return null;
  }

  return data;
}

export async function fetchWebpages(userId: string): Promise<Webpage[]> {
  const { data, error } = await supabase
    .from('webpages')
    .select(WEBPAGE_READ_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching webpages:', error);
    return [];
  }

  return data || [];
}

export async function fetchWebpagesPage(
  userId: string,
  offset: number,
  limit: number = 10
): Promise<{ data: Webpage[]; hasMore: boolean }> {
  const { data, error } = await supabase
    .from('webpages')
    .select(WEBPAGE_READ_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching webpages page:', error);
    return { data: [], hasMore: false };
  }

  return {
    data: data ?? [],
    hasMore: (data?.length ?? 0) === limit,
  };
}

export async function fetchWebpagesCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('webpages')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching webpages count:', error);
    return 0;
  }

  return count ?? 0;
} 
